import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import googleDriveService from '../services/googleDrive';
import DocumentViewer from './DocumentViewer';
import Sidebar from './Sidebar';
import { useAuth } from './Auth';

const ProcessPage = () => {
  const { processName } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [folderData, setFolderData] = useState(null);
  const [sidebarData, setSidebarData] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert URL slug back to folder name
  const getFolderName = (slug) => {
    return slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  useEffect(() => {
    if (user) {
      loadProcessData();
    } else {
      setLoading(false);
    }
  }, [processName, user]);

  const loadProcessData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const folderName = getFolderName(processName);
      console.log(`🎯 Loading process: ${folderName}`);

      // Get the drive ID based on the current route
      const routeSection = location.pathname.split('/')[1]; // e.g., 'technical-processes'
      const driveId = googleDriveService.getDriveIdFromRoute(routeSection);
      
      const processData = await googleDriveService.getProcessFolderContents(folderName, driveId);
      setFolderData(processData.folder);

      // Build sidebar structure
      const folders = processData.contents.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
      console.log(`📁 Found ${folders.length} folders in ${folderName}:`, folders.map(f => f.name));
      
      const sidebarSections = [];

      for (const folder of folders) {
        const documents = await googleDriveService.getFolderDocuments(folder.id);
        
        // Recursive function to load subfolders and their subfolders
        const loadSubfoldersRecursively = async (parentFolder, depth = 0) => {
          const maxDepth = 3; // Prevent infinite recursion
          if (depth >= maxDepth) return [];
          
          const subfolderContents = await googleDriveService.getFolderContents(parentFolder.id);
          const subfolders = subfolderContents.files.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
          console.log(`📂 ${'  '.repeat(depth)}Folder "${parentFolder.name}" has ${subfolders.length} subfolders:`, subfolders.map(f => f.name));
          
          const subfoldersWithDocs = await Promise.all(
            subfolders.map(async (subfolder) => {
              // Load documents in this subfolder
              const subfolderDocs = await googleDriveService.getFolderDocuments(subfolder.id);
              const documents = subfolderDocs.files || [];
              console.log(`📄 ${'  '.repeat(depth + 1)}Subfolder "${subfolder.name}" has ${documents.length} documents:`, documents.map(d => d.name));
              
              // Recursively load sub-subfolders
              const nestedSubfolders = await loadSubfoldersRecursively(subfolder, depth + 1);
              
              return {
                ...subfolder,
                documents: documents,
                subfolders: nestedSubfolders
              };
            })
          );
          
          return subfoldersWithDocs;
        };
        
        // Load subfolders recursively
        const subfoldersWithDocs = await loadSubfoldersRecursively(folder);
        
        // Always show folders, even if empty
        sidebarSections.push({
          folder: folder,
          documents: documents.files || [],
          subfolders: subfoldersWithDocs || []
        });
      }

      setSidebarData(sidebarSections);
      
      // Auto-expand first section
      if (sidebarSections.length > 0 && sidebarSections[0].documents.length > 0) {
        // Don't auto-select document, let user choose
      }

    } catch (err) {
      console.error('Error loading process:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  if (!user) {
    return (
      <div className="process-page">
        <div className="workspace-container">
          <div className="workspace-content">
            <div className="welcome-message">
              <h2>{getFolderName(processName)}</h2>
              <p>Please sign in with Google to view this process documentation.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="process-page loading">
        <h2>Loading {getFolderName(processName)}...</h2>
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="process-page error">
        <h2>Error Loading Process</h2>
        <p>Could not load {getFolderName(processName)}: {error}</p>
        <button onClick={loadProcessData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="process-page">
      <div className="workspace-container">
        
        <Sidebar 
          sections={sidebarData}
          onDocumentSelect={handleDocumentSelect}
          selectedDocument={selectedDocument}
          onDocumentCreated={(document) => {
            console.log('Document created:', document);
            loadProcessData(); // Reload to show new document
          }}
        />

        <div className="workspace-content">
          {selectedDocument ? (
            <DocumentViewer document={selectedDocument} />
          ) : (
            <div className="welcome-message">
              <h2>{folderData?.name || getFolderName(processName)}</h2>
              <p>Select a document from the sidebar to view its content.</p>
              {sidebarData.length === 0 && (
                <p><em>No documents found in this process folder.</em></p>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ProcessPage;